import {
  CATEGORIAS_SERVICIO,
  CATEGORIAS_SERVICIO_CORTO,
  formatearMoneda,
  type CategoriaServicio,
} from "@uirtus/shared";
import { useEffect, useMemo, useState } from "react";
import { FormularioServicio } from "../componentes/formulario-servicio";
import { Eye, EyeOff, Pencil, Trash2 } from "lucide-react";
import {
  Aviso,
  Boton,
  BotonIcono,
  Confirmacion,
  Entrada,
  Etiqueta,
  Modal,
  Pestanas,
  Seleccion,
  type Pestana,
} from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import {
  useActualizarServicio,
  useCambiarActivoServicio,
  useCrearServicio,
  useEliminarServicio,
  useServicios,
  type Servicio,
} from "../lib/servicios";
import { useDebounce } from "../lib/use-debounce";

type FiltroEstado = "activos" | "inactivos" | "todos";

const A_BOOLEANO: Record<FiltroEstado, boolean | undefined> = {
  activos: true,
  inactivos: false,
  todos: undefined,
};

const CATEGORIAS = Object.keys(CATEGORIAS_SERVICIO) as CategoriaServicio[];

export function Servicios() {
  const [texto, setTexto] = useState("");
  const [estado, setEstado] = useState<FiltroEstado>("activos");
  const [categoria, setCategoria] = useState<CategoriaServicio>("DESARROLLO");

  const [creando, setCreando] = useState(false);
  const [editando, setEditando] = useState<Servicio | null>(null);
  const [aBorrar, setABorrar] = useState<Servicio | null>(null);
  const [mensaje, setMensaje] = useState<string | null>(null);

  const busqueda = useDebounce(texto, 300);

  /**
   * La categoría NO se manda a la API: se traen todos los servicios que pasan
   * el resto de filtros y el reparto en pestañas se hace aquí. Así los
   * contadores de cada pestaña siempre son reales y cambiar de categoría no
   * dispara una consulta. El catálogo es de decenas de registros, no miles.
   */
  const { data, isLoading, isError, error } = useServicios({
    q: busqueda || undefined,
    activo: A_BOOLEANO[estado],
  });

  const crear = useCrearServicio();
  const actualizar = useActualizarServicio();
  const cambiarActivo = useCambiarActivoServicio();
  const eliminar = useEliminarServicio();

  const servicios = useMemo(() => data ?? [], [data]);

  const porCategoria = useMemo(() => {
    const grupos = new Map<CategoriaServicio, Servicio[]>();
    for (const cat of CATEGORIAS) grupos.set(cat, []);
    for (const s of servicios) grupos.get(s.categoria)?.push(s);
    return grupos;
  }, [servicios]);

  const pestanas: Pestana<CategoriaServicio>[] = CATEGORIAS.map((cat) => ({
    clave: cat,
    texto: CATEGORIAS_SERVICIO_CORTO[cat],
    conteo: porCategoria.get(cat)?.length ?? 0,
  }));

  const visibles = porCategoria.get(categoria) ?? [];
  const enOtrasCategorias = servicios.length - visibles.length;

  // Al crear un servicio, saltar a su categoría: si no, la pantalla parece
  // no haber hecho nada porque el nuevo cayó en otra pestaña.
  const irACategoriaDe = (s: Servicio) => setCategoria(s.categoria);

  // Si un filtro deja la pestaña activa vacía pero hay resultados en otra, no
  // se cambia sola: se avisa. Mover al usuario sin que lo pida desorienta.
  useEffect(() => {
    if (busqueda === "" && visibles.length === 0 && servicios.length > 0) {
      const conDatos = CATEGORIAS.find(
        (c) => (porCategoria.get(c)?.length ?? 0) > 0,
      );
      if (conDatos) setCategoria(conDatos);
    }
  }, [busqueda, visibles.length, servicios.length, porCategoria]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Servicios</h1>
          <p className="text-sm text-slate-500">
            El catálogo del que salen los conceptos de cotizaciones y facturas.
          </p>
        </div>
        <Boton onClick={() => setCreando(true)}>Nuevo servicio</Boton>
      </div>

      <div className="flex flex-wrap gap-2">
        <Entrada
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscar por clave, nombre o descripción"
          className="w-full sm:w-96"
        />
        <Seleccion
          value={estado}
          onChange={(e) => setEstado(e.target.value as FiltroEstado)}
          className="w-full sm:w-36"
        >
          <option value="activos">Activos</option>
          <option value="inactivos">Inactivos</option>
          <option value="todos">Todos</option>
        </Seleccion>
      </div>

      {mensaje && <Aviso tono="alerta">{mensaje}</Aviso>}

      {isError && (
        <Aviso>
          {error instanceof ErrorApi
            ? error.message
            : "No se pudo cargar el catálogo"}
        </Aviso>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : isError ? null : (
        <div>
          <Pestanas
            pestanas={pestanas}
            activa={categoria}
            alCambiar={setCategoria}
          />

          <div
            role="tabpanel"
            aria-label={CATEGORIAS_SERVICIO[categoria]}
            className="pt-4"
          >
            {visibles.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 px-4 py-12 text-center dark:border-slate-700">
                <p className="text-sm text-slate-500">
                  {busqueda
                    ? `Ningún servicio de ${CATEGORIAS_SERVICIO[categoria].toLowerCase()} coincide con "${busqueda}".`
                    : `Todavía no hay servicios en ${CATEGORIAS_SERVICIO[categoria].toLowerCase()}.`}
                </p>

                {/* Con una búsqueda activa, lo más probable es que las
                    coincidencias estén en otra pestaña. Decirlo evita concluir
                    que no existe lo que se busca. */}
                {busqueda && enOtrasCategorias > 0 && (
                  <p className="mt-2 text-sm text-slate-500">
                    Hay {enOtrasCategorias} coincidencia
                    {enOtrasCategorias === 1 ? "" : "s"} en{" "}
                    {CATEGORIAS.filter(
                      (c) => c !== categoria && (porCategoria.get(c)?.length ?? 0) > 0,
                    ).map((c, i, lista) => (
                      <span key={c}>
                        <button
                          onClick={() => setCategoria(c)}
                          className="text-marca-600 underline hover:text-marca-700"
                        >
                          {CATEGORIAS_SERVICIO[c]}
                        </button>
                        {i < lista.length - 1 ? ", " : ""}
                      </span>
                    ))}
                    .
                  </p>
                )}
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {visibles.map((s) => (
                      <tr key={s.id} className={s.activo ? "" : "opacity-60"}>
                        <td className="w-40 px-4 py-3 align-top font-mono text-xs text-slate-500">
                          {s.clave}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{s.nombre}</span>
                            {s.esRecurrente && (
                              <Etiqueta tono="alerta">Recurrente</Etiqueta>
                            )}
                            {!s.activo && <Etiqueta>Inactivo</Etiqueta>}
                          </div>
                          <p className="mt-0.5 text-xs text-slate-500">
                            {s.descripcion}
                          </p>
                          <p className="mt-1 font-mono text-[11px] text-slate-400">
                            SAT {s.claveProdServ} · {s.claveUnidad}
                            {s.unidad ? ` (${s.unidad})` : ""}
                            {s.tasaIva
                              ? ` · IVA ${(Number(s.tasaIva) * 100).toFixed(0)}%`
                              : " · exento"}
                          </p>
                        </td>
                        <td className="w-36 whitespace-nowrap px-4 py-3 text-right align-top font-mono">
                          {s.precioBase ? (
                            formatearMoneda(s.precioBase, s.moneda)
                          ) : (
                            <span className="text-xs text-slate-400">
                              sin tarifa
                            </span>
                          )}
                        </td>
                        <td className="w-28 px-4 py-3 text-right align-top">
                          <div className="flex justify-end gap-0.5">
                            <BotonIcono
                              etiqueta="Editar servicio"
                              onClick={() => setEditando(s)}
                            >
                              <Pencil size={16} />
                            </BotonIcono>
                            <BotonIcono
                              etiqueta={
                                s.activo
                                  ? "Desactivar: deja de aparecer al cotizar"
                                  : "Reactivar"
                              }
                              disabled={cambiarActivo.isPending}
                              onClick={() =>
                                cambiarActivo.mutate({
                                  id: s.id,
                                  activo: !s.activo,
                                })
                              }
                            >
                              {s.activo ? <EyeOff size={16} /> : <Eye size={16} />}
                            </BotonIcono>
                            {/* Borrar solo aparece en los ya desactivados:
                                obliga a un paso previo antes de algo definitivo. */}
                            {!s.activo && (
                              <BotonIcono
                                etiqueta="Borrar definitivamente"
                                variante="peligro"
                                onClick={() => setABorrar(s)}
                              >
                                <Trash2 size={16} />
                              </BotonIcono>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        titulo="Nuevo servicio"
        abierto={creando}
        alCerrar={() => {
          setCreando(false);
          crear.reset();
        }}
      >
        <FormularioServicio
          categoriaInicial={categoria}
          enviando={crear.isPending}
          errorServidor={crear.error ? crear.error.message : null}
          alEnviar={(datos) =>
            crear.mutate(datos as never, {
              onSuccess: (creado) => {
                setCreando(false);
                crear.reset();
                irACategoriaDe(creado);
              },
            })
          }
          alCancelar={() => {
            setCreando(false);
            crear.reset();
          }}
        />
      </Modal>

      <Modal
        titulo="Editar servicio"
        abierto={!!editando}
        alCerrar={() => {
          setEditando(null);
          actualizar.reset();
        }}
      >
        {editando && (
          <FormularioServicio
            servicio={editando}
            enviando={actualizar.isPending}
            errorServidor={actualizar.error ? actualizar.error.message : null}
            alEnviar={(datos) =>
              actualizar.mutate({ id: editando.id, ...datos } as never, {
                onSuccess: (guardado) => {
                  setEditando(null);
                  actualizar.reset();
                  irACategoriaDe(guardado);
                },
              })
            }
            alCancelar={() => {
              setEditando(null);
              actualizar.reset();
            }}
          />
        )}
      </Modal>

      <Confirmacion
        abierto={!!aBorrar}
        titulo="Eliminar servicio"
        mensaje={
          <>
            Se va a borrar <strong>{aBorrar?.nombre}</strong>. Si ya se usó en
            alguna cotización o factura, la API lo va a impedir.
          </>
        }
        textoConfirmar="Eliminar"
        peligroso
        ocupado={eliminar.isPending}
        alCancelar={() => setABorrar(null)}
        alConfirmar={() =>
          aBorrar &&
          eliminar.mutate(aBorrar.id, {
            onSuccess: () => setABorrar(null),
            onError: (e) => {
              setABorrar(null);
              setMensaje(e.message);
            },
          })
        }
      />
    </div>
  );
}
